const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/** `Research.MemberProfile.PreviousVisitAt` age at which a "returning" greeting becomes "away". */
const AWAY_MIN_DAYS = 3;
/** Age past which "away" becomes "long-away" — strictly more than 30 days. */
const LONG_AWAY_MIN_DAYS = 30;
/** Consecutive-day streak that turns a recent visit into the "regular" (on-a-roll) greeting (P/Member_Hub.html:1186). */
const REGULAR_MIN_STREAK = 3;
/** "Nth visit this month" is capped here, as the prototype caps it (P/Member_Hub.html:1232-1233). */
const REGULAR_VISIT_CAP = 24;

/** What `GreetingFor` needs from `ResearchHome` — every field it actually reads, named for the greeting rather than the wire shape. */
export interface HomeGreetingInput {
    displayName: string;
    visitCount: number;
    previousVisitAt: string | null;
    /** Sum of `newGrants + newPeople + newJobs`. */
    newMatches: number;
    /** Saved grants closing within 30 days — the milestone greeting's "close soon". */
    savedClosingSoon: number;
    /** Saved grants closing within 14 days — the away greeting's "in the next two weeks". */
    savedClosingSoonTwoWeeks: number;
    /** Consecutive days with a visit, ending today (server-derived from the member's page views). */
    visitStreak: number;
    /** Distinct days with a visit this calendar month — the regular greeting's "Nth visit this month". */
    visitsThisMonth: number;
}

/**
 * One segment of a greeting's body text. The template renders `<strong>` for
 * `strong` segments and a real navigable link (via the host adapter's
 * `NavigateTo`) for `linkModuleKey` segments — never `[innerHTML]` — so this
 * pure helper stays free of both Angular and any markup string.
 */
export interface HomeGreetingBodyPart {
    readonly text: string;
    readonly strong: boolean;
    /** Module key this segment should navigate to (first-visit's "Grants"/"Collaborators"/"Jobs" mentions), or null for a plain/bold segment. */
    readonly linkModuleKey: string | null;
}

export interface HomeGreeting {
    heading: string;
    /** Plain-text concatenation of `bodyParts` — no markup, no bold/link markers. */
    body: string;
    bodyParts: readonly HomeGreetingBodyPart[];
    /** The stat chip's emoji (P/Member_Hub.html:310, `.gstat .ge`): 🎉 first, 👋 away, 🔥 regular, the hour's `DayEmoji` when returning. */
    emoji: string | null;
    /** The stat chip's text (`.gstat .gt`), or null when the chip shows the emoji alone (returning). */
    tag: string | null;
    tagKind: 'welcome' | 'away' | 'milestone' | null;
}

function Plain(text: string): HomeGreetingBodyPart {
    return { text, strong: false, linkModuleKey: null };
}

function Strong(text: string): HomeGreetingBodyPart {
    return { text, strong: true, linkModuleKey: null };
}

function Link(text: string, moduleKey: string): HomeGreetingBodyPart {
    return { text, strong: false, linkModuleKey: moduleKey };
}

function PartOfDay(now: Date): 'Good morning' | 'Good afternoon' | 'Good evening' {
    const hour = now.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
}

/** The hour's emoji (P/Member_Hub.html:1166): 🌙 before 5, ☀️ before 12, 🌤️ before 17, 🌇 before 22, 🌙 after. Local hour, like `PartOfDay`. */
export function DayEmoji(now: Date): string {
    const hour = now.getHours();
    if (hour < 5) return '🌙';
    if (hour < 12) return '☀️';
    if (hour < 17) return '🌤️';
    if (hour < 22) return '🌇';
    return '🌙';
}

/** "1st"/"2nd"/"3rd"/"Nth" suffix, with the 11-13 exception. */
function Ordinal(n: number): string {
    const rem100 = n % 100;
    if (rem100 >= 11 && rem100 <= 13) return 'th';
    switch (n % 10) {
        case 1:
            return 'st';
        case 2:
            return 'nd';
        case 3:
            return 'rd';
        default:
            return 'th';
    }
}

/**
 * "Today is Wednesday, 19 August 2026" — `en-GB` orders day-before-month with a
 * comma after the weekday, matching the mockup's own `dateLine()`.
 *
 * The `returning` and `regular` bodies below OPEN with this sentence, which is
 * the mockup's own `.sub2` (parity/home.md §4.2) — so Home renders no separate
 * date element and passes no `Date` to `agu-plain-header`, or the member would
 * read the same sentence twice on one header. It is exported for the screens
 * that show a date WITHOUT a greeting (Saved / Search / Settings), which do use
 * that input, so both paths word the date identically.
 */
export function DateLine(now: Date): string {
    const formatted = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    return `Today is ${formatted}`;
}

/** "1 saved grant" / "N saved grants" — the A-J round read `1 saved grants close soon.` (2026-10-05). */
function SavedGrants(count: number): string {
    return `${count} saved grant${count === 1 ? '' : 's'}`;
}

/** "1 new match" / "N new matches" (P/Member_Hub.html:1217, :1222) — the suite pre-run read `1 new matches` (2026-11-03). */
function NewMatches(count: number): string {
    return `${count} new match${count === 1 ? '' : 'es'}`;
}

function DaysSince(previous: Date, now: Date): number {
    return Math.floor((now.getTime() - previous.getTime()) / MILLISECONDS_PER_DAY);
}

function Build(heading: string, bodyParts: readonly HomeGreetingBodyPart[], emoji: string, tag: string | null, tagKind: HomeGreeting['tagKind']): HomeGreeting {
    return { heading, body: bodyParts.map((p) => p.text).join(''), bodyParts, emoji, tag, tagKind };
}

/**
 * The five greeting states the Member Hub Home page can show, derived
 * purely from `ResearchHome` and the current clock — no Angular, no I/O, so
 * it is computed once per load into fields (never a template getter reading
 * `Date.now()`, per ui-best-practices §7).
 *
 * Precedence mirrors the prototype's own state-selection order
 * (P/Member_Hub.html:1181-1186): first visit, then how long the member has
 * been away (which wins over a streak), then whether they are on a streak of
 * three or more consecutive days, and only then the plain "returning"
 * default. The away thresholds stay 3 and >30 days.
 */
export function GreetingFor(input: HomeGreetingInput, now: Date): HomeGreeting {
    const name = input.displayName;

    // First visit, or no recorded previous visit at all. `previousVisitAt` is
    // what every "returning" variant below measures against — "since you were
    // last here", "N days away" — so with nothing to measure they would each
    // state something untrue. A staff account has no MemberProfile row and the
    // server reports it as a first visit (visitCount 1, previousVisitAt null);
    // live finding #5 was that shape falling through to the returning default.
    if (input.visitCount <= 1 || input.previousVisitAt === null) {
        return Build(
            `Welcome to the Member Hub, ${name}`,
            [
                Plain('This is your hub for the three tools AGU members use most: '),
                Link('Grants', 'grants'),
                Plain(' for funding matched to your work, '),
                Link('People', 'people'),
                Plain(' to find co-authors, and '),
                Link('Jobs', 'jobs'),
                Plain(' across the Earth and space sciences. Everything you save lands in one place.'),
            ],
            '🎉', // P/Member_Hub.html:1213
            'First visit',
            'welcome'
        );
    }

    const previous = input.previousVisitAt ? new Date(input.previousVisitAt) : null;
    const daysAway = previous ? DaysSince(previous, now) : 0;

    if (daysAway > LONG_AWAY_MIN_DAYS) {
        return Build(
            `Good to see you again, ${name}`,
            [
                Plain("It's been a while, we kept matching in the background. "),
                Strong(`${input.newMatches} ${input.newMatches === 1 ? 'opportunity' : 'opportunities'}`), // P/Member_Hub.html:1227
                Plain(`${input.newMatches === 1 ? ' is' : ' are'} waiting, and your saved items are still tracked.`),
            ],
            '👋', // P/Member_Hub.html:1228: `daysAway ? daysAway + ' days away' : …` — a long-away greeting always has its day count here
            `${daysAway} days away`,
            'away'
        );
    }

    if (daysAway >= AWAY_MIN_DAYS) {
        return Build(
            `Welcome back, ${name}`,
            [
                Plain("Here's what you missed: "),
                Strong(NewMatches(input.newMatches)),
                Plain(' across your tools, and '),
                Strong(SavedGrants(input.savedClosingSoonTwoWeeks)),
                Plain(`${input.savedClosingSoonTwoWeeks === 1 ? ' is' : ' are'} closing in the next two weeks.`),
            ],
            '👋', // P/Member_Hub.html:1223
            `${daysAway} days away`,
            'away'
        );
    }

    if (input.visitStreak >= REGULAR_MIN_STREAK) {
        // P/Member_Hub.html:1230-1236. The prototype floors its localStorage counter at 5; the
        // monthly count here is real, so only the cap stays (it is at least 1: today is a visit).
        const n = Math.min(Math.max(input.visitsThisMonth, 1), REGULAR_VISIT_CAP);
        return Build(
            `You're on a roll, ${name}`,
            [
                Plain(`${DateLine(now)} · ${Math.max(input.visitStreak, REGULAR_MIN_STREAK)} days in a row. `),
                Strong(SavedGrants(input.savedClosingSoon)),
                Plain(input.savedClosingSoon === 1 ? ' closes soon.' : ' close soon.'),
            ],
            '🔥',
            `${n}${Ordinal(n)} visit this month`,
            'milestone'
        );
    }

    // P/Member_Hub.html:1216-1218: the hour's emoji, no stat text.
    return Build(
        `${PartOfDay(now)}, ${name}`,
        [Plain(`${DateLine(now)} · `), Strong(NewMatches(input.newMatches)), Plain(' since you were last here.')],
        DayEmoji(now),
        null,
        null
    );
}
